import React, { createContext, useContext, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AnimatedButton } from '@/components/ui/animated-button';
import { RegistrationModal } from '@/components/registration/RegistrationModal';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';

interface AuthModalContextType {
  showSignIn: () => void;
  showRegister: () => void;
  hideModals: () => void;
}

const AuthModalContext = createContext<AuthModalContextType | undefined>(undefined);

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
}

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      
      toast.success('Signed in successfully');
      setShowSignInModal(false);
      setEmail('');
      setPassword('');
      navigate('/user/dashboard');
    } catch (error: any) {
      console.error('Sign in failed:', error.message);
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    showSignIn: () => {
      setShowRegisterModal(false);
      setShowSignInModal(true);
    },
    showRegister: () => {
      setShowSignInModal(false);
      setShowRegisterModal(true);
    },
    hideModals: () => {
      setShowSignInModal(false);
      setShowRegisterModal(false);
      setEmail('');
      setPassword('');
    }
  };

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      
      {/* Sign In Modal */}
      {showSignInModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <Card className="w-full max-w-md relative animate-fade-in">
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-2 top-2" 
              onClick={() => setShowSignInModal(false)}
            >
              <X className="h-4 w-4" />
            </Button>
            
            <CardHeader>
              <CardTitle>Sign In</CardTitle>
              <CardDescription>
                Enter your credentials to access the platform
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleAuth} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    required 
                    autoComplete="email" 
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required 
                    autoComplete="current-password" 
                  />
                  <div className="text-right">
                    <Link 
                      to="/reset-password" 
                      className="text-xs text-primary hover:underline"
                      onClick={() => setShowSignInModal(false)}
                    >
                      Forgot password?
                    </Link>
                  </div>
                </div>
                
                <AnimatedButton 
                  type="submit" 
                  className="w-full" 
                  variant="teal" 
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </AnimatedButton>
                
                <div className="text-center mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowSignInModal(false);
                      setShowRegisterModal(true);
                    }}
                    className="text-sm text-primary hover:underline"
                  >
                    Need an account? Register
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Registration Modal */}
      {showRegisterModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 p-4">
          <RegistrationModal 
            onClose={() => setShowRegisterModal(false)} 
            onSwitchToSignIn={() => {
              setShowRegisterModal(false);
              setShowSignInModal(true);
            }}
          />
        </div>
      )}
    </AuthModalContext.Provider>
  );
}
